import { Location } from 'history';
import { call, put, takeEvery, CancelledEffect, cancelled, fork, takeLatest } from 'redux-saga/effects';

import { appDataModel, setBooksData, setFirestoreUserData } from '@entities/app-data';
import { authModel } from '@entities/auth-operation';
import { setUserData } from '@entities/auth-user';
import { redirect, redirectActions } from '@entities/history';
import { signOut } from '@features/auth/log-out';
import { bookDeletionModel } from '@features/book-deletion';
import { exportModel } from '@features/book-export';
import { profileSettingModel } from '@features/profile-settings';
import { authBreadcrumb, captureAuthError } from '@features/sentry-connect';
import { snackbarModel, createNotification } from '@features/snackbar';
import { clearData, onHistory, onNavigate, privateRouteHandlers } from '@processes/routing';
import { supportModel } from '@features/support';
import { mixpanelFirstIdentify } from '@features/mixpanel/saga';

function* runStep(stepName: string, saga: () => Generator) {
  authBreadcrumb(`handleSignedIn: step start - ${stepName}`);
  try {
    yield call(saga);
    authBreadcrumb(`handleSignedIn: step done - ${stepName}`);
  } catch (error: unknown) {
    captureAuthError(error, `handleSignedIn:${stepName}`, {
      message: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

export function* handleSignedIn() {
  authBreadcrumb('handleSignedIn: entered');
  yield takeLatest(appDataModel.actions.setSelectedBookId, appDataModel.sagas.watchDocsWorker);
  yield takeLatest(appDataModel.actions.setSelectedBookId, appDataModel.sagas.watchAppendicesWorker);
  yield takeLatest(appDataModel.actions.setSelectedBookId, appDataModel.sagas.watchFoldersWorker);
  yield takeLatest(
    [appDataModel.actions.setDocs, appDataModel.actions.setFolders],
    appDataModel.sagas.watchBookStructure
  );

  yield takeEvery(authModel.actions.signOutClick, signOut);
  yield takeEvery(profileSettingModel.actions.updateNamesAction, profileSettingModel.sagas.updateUserFirstAndLastName);
  yield takeEvery(redirectActions.toEditBookClick, function* (action) {
    yield call(redirect.toBookEdit, action.payload.bookId);
  });
  yield takeEvery(redirectActions.backFromBookEditClick, redirect.backFromBookEdit);

  // Get initial data for the app
  try {
    yield call(runStep, 'setUserData', setUserData);
    yield call(runStep, 'setBooksData', setBooksData);
    yield call(runStep, 'setFirestoreUserData', setFirestoreUserData);
    yield call(runStep, 'mixpanelFirstIdentify', mixpanelFirstIdentify);
  } catch (error: unknown) {
    // Initial data load failed (e.g. missing /users/{uid} doc - orphan auth user).
    // Without this data the app would render a forever-loader, so bail out cleanly:
    // notify the user, sign them out, and send them back to /login.
    authBreadcrumb('handleSignedIn: initial data failed, signing out', {
      message: error instanceof Error ? error.message : String(error),
    });
    yield put(
      snackbarModel.actions.addNotificationAction(
        createNotification('error', 'Your account is not fully set up. Please contact support if this keeps happening.')
      )
    );
    yield call(signOut);
    return;
  }
  authBreadcrumb('handleSignedIn: initial data ready, forking watchers');
  // Watch books
  yield fork(appDataModel.sagas.watchBooksWorker);
  yield fork(exportModel.saga);
  yield fork(bookDeletionModel.saga);
  yield fork(appDataModel.sagas.watchFirestoreUser);
  yield fork(supportModel.saga);
  try {
    yield call(
      onHistory,
      function* (location: Location<string>) {
        yield call(onNavigate, location, privateRouteHandlers, redirect.to404);
      },
      'Private'
    );
  } finally {
    const isCancel: CancelledEffect = yield cancelled();
    if (isCancel) {
      yield call(clearData);
    }
  }
}
